// Chat REST API module: cancel a run/thread and answer a HITL
// question/approval. Sending/streaming a turn is no longer a REST concern —
// it goes through the AgentHub native stream (see
// shared/chat-runtime/useNativeAgenthubExternalRuntime.ts) instead of this module.

import type { ApiClient } from '@/shared/api';
import type { CancelResult } from '../model/types';

export interface ChatApi {
  cancelRun(runId: string, signal?: AbortSignal): Promise<CancelResult>;
  cancelThread(threadId: string, signal?: AbortSignal): Promise<CancelResult>;
}

export interface ChatApiOptions {
  client: ApiClient;
}

function isCancelStatus(value: unknown): value is CancelResult['status'] {
  if (typeof value !== 'string') return false;
  if (value === 'cancelling' || value === 'not_found') return true;
  return value.startsWith('already_');
}

function toCancelResult(raw: unknown): CancelResult {
  const record = isRecord(raw) ? raw : {};
  const status = isCancelStatus(record.status) ? record.status : 'not_found';
  const result: CancelResult = { status };
  if (typeof record.request_id === 'string') result.requestId = record.request_id;
  if (typeof record.thread_id === 'string') result.threadId = record.thread_id;
  if (typeof record.run_id === 'string') result.runId = record.run_id;
  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function createChatApi(opts: ChatApiOptions): ChatApi {
  return {
    async cancelRun(runId, signal) {
      const raw = await opts.client.request({
        method: 'POST',
        path: `/api/v1/runs/${encodeURIComponent(runId)}/cancel`,
        signal,
      });
      return toCancelResult(raw);
    },

    async cancelThread(threadId, signal) {
      const raw = await opts.client.request({
        method: 'POST',
        path: `/api/v1/threads/${encodeURIComponent(threadId)}/cancel`,
        signal,
      });
      return toCancelResult(raw);
    },

  };
}
